<template>
  <div class="lottery-container" :class="{ 'spinning': isSpinning }">
    <!-- 标题 -->
    <div class="lottery-header">
      <h1>
        <i class="fas fa-gift gift-icon"></i>
        幸运大转盘
      </h1>
      <p class="subtitle">{{ title }}</p>
    </div>

    <!-- 轮盘主体区域 -->
    <div class="wheel-wrapper">
      <!-- 1. 跑马灯层 -->
      <div ref="lightsContainer" class="lights-container"></div>

      <!-- 2. 轮盘层 -->
      <div class="wheel-container">
        <canvas
          ref="wheelCanvas"
          :width="canvasSize"
          :height="canvasSize"
          class="wheel-canvas"
          :style="{ transform: `rotate(${currentRotation}deg)` }"
        ></canvas>
      </div>

      <!-- 3. 中心交互层 (指针+按钮) -->
      <div class="center-hub">
        <!-- 指针：在按钮上方 -->
        <div class="center-pointer"></div>
        <!-- 按钮：在指针下方 -->
        <button
          :disabled="isSpinning || isProcessing"
          @click="startSpin"
          class="spin-button"
          :class="{ 'disabled': isSpinning || isProcessing }"
        >
          <span v-if="!isSpinning && !isProcessing">抽奖</span>
          <span v-else class="spinning-text">
            <i class="fas fa-spinner spinner-icon"></i>
            {{ isProcessing ? '处理中...' : '抽奖中...' }}
          </span>
        </button>
      </div>
    </div>

    <!-- 金币信息 -->
    <div class="coins-info">
      <div class="coins-display">
        <i class="fas fa-coins coins-icon"></i>
        <span class="coins-text">金币:
          <span class="coins-amount">{{ currentCoins }}</span>
        </span>
        <span class="separator">|</span>
        <span class="cost-text">消耗:
          <span class="cost-amount">{{ costPerSpin }}</span>
        </span>
      </div>
    </div>

    <!-- 结果弹窗 - 精简优化 -->
    <transition name="popup-fade">
      <div v-if="showPopup" class="result-popup-overlay">
        <div class="overlay-bg" @click="closePopup"></div>
        <div
          :class="[
            'result-popup-content',
            popupAnimationClass,
            selectedPrize.productValue > 0 ? 'popup-win' : 'popup-lose'
          ]"
        >
          <div class="popup-inner">
            <div :class="popupIconClass">
              <i :class="popupIcon"></i>
            </div>
            <h3 class="popup-title">{{ popupTitle }}</h3>
            <p class="popup-subtitle">{{ popupSubtitle }}</p>

            <div :class="prizeResultClass">
              <div class="prize-image-container">
                <img
                  :src="selectedPrize.picUrl"
                  :alt="selectedPrize.prizeName"
                  class="prize-image"
                  @error="handleImageError"
                >
              </div>
              <p class="prize-name">{{ selectedPrize.prizeName }}</p>
              <p v-if="selectedPrize.productValue > 0" class="prize-value">
                价值: {{ selectedPrize.productValue }}元
              </p>
            </div>

            <button
              @click="closePopup"
              :class="popupBtnClass"
            >
              {{ popupBtnText }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'LuckyWheel',
  props: {
    // 初始金币数量
    initialCoins: {
      type: Number,
      default: 1000
    },
    // 每次抽奖消耗金币
    spinCost: {
      type: Number,
      default: 100
    },
    // 轮盘标题
    title: {
      type: String,
      default: '点击中心按钮，开启好运'
    },
    // 自定义奖品列表
    customPrizes: {
      type: Array,
      default: () => null
    },
    // 是否启用后台控制模式
    enableBackendControl: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      prizes: this.processPrizes(this.customPrizes),
      currentCoins: this.initialCoins,
      costPerSpin: this.spinCost,
      isSpinning: false,
      isProcessing: false,
      currentRotation: 0,
      canvasSize: 320,
      wheelRadius: 160,
      lightInterval: null,
      showPopup: false,
      selectedPrize: {},
      popupAnimationClass: 'popup-hidden',
      fallbackImages: [
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0ZGNkI2QiIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSJ3aGl0ZSI+aVBob25lPC90ZXh0Pjwvc3ZnPg==',
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzRFQ0RDNCIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSJ3aGl0ZSI+QWlyUG9kczwvdGV4dD48L3N2Zz4=',
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0ZGRTY2RCIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSJ3aGl0ZSI+U3RhcmJ1Y2tzPC90ZXh0Pjwvc3ZnPg==',
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzk1RTFEMyIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSJ3aGl0ZSI+SmQuY29tPC90ZXh0Pjwvc3ZnPg==',
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0YzODE4MSIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSJ3aGl0ZSI+NTAg5Lu2PC90ZXh0Pjwvc3ZnPg==',
        'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyMDAgMjAwIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI0FBOTZEQSIvPjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSJ3aGl0ZSI+6L+U5Zue5rKZ5bGFPC90ZXh0Pjwvc3ZnPg=='
      ],
      // 图片加载状态跟踪
      imageLoadPromises: [],
      allImagesLoaded: false
    };
  },
  computed: {
    popupIconClass() {
      return this.selectedPrize.productValue > 0
        ? 'popup-icon popup-icon-win'
        : 'popup-icon popup-icon-lose';
    },
    popupIcon() {
      return this.selectedPrize.productValue > 0
        ? 'fas fa-crown'
        : 'fas fa-leaf';
    },
    popupTitle() {
      return this.selectedPrize.productValue > 0 ? '恭喜！' : '很遗憾';
    },
    popupSubtitle() {
      return this.selectedPrize.productValue > 0 ? '恭喜中奖' : '下次好运';
    },
    popupBtnText() {
      return this.selectedPrize.productValue > 0 ? '太棒了' : '知道了';
    },
    prizeResultClass() {
      return this.selectedPrize.productValue > 0 ? 'prize-result prize-result-win' : 'prize-result prize-result-lose';
    },
    popupBtnClass() {
      return this.selectedPrize.productValue > 0
        ? 'popup-btn popup-btn-win'
        : 'popup-btn popup-btn-lose';
    }
  },
  watch: {
    customPrizes: {
      handler(newPrizes) {
        if (newPrizes) {
          this.prizes = this.processPrizes(newPrizes);
          this.$nextTick(() => {
            this.loadAllImages().then(() => {
              this.initWheel();
            });
          });
        }
      },
      deep: true
    }
  },
  mounted() {
    console.log('轮盘组件已挂载');
    this.initLights();

    // 先加载所有图片，然后再初始化轮盘
    this.loadAllImages().then(() => {
      console.log('所有图片加载完成，开始绘制轮盘');
      this.initWheel();
    }).catch(error => {
      console.error('图片加载失败，使用默认图片:', error);
      this.initWheel();
    });
  },
  beforeDestroy() {
    if (this.lightInterval) {
      clearInterval(this.lightInterval);
    }
  },
  methods: {
    // 处理奖品数据，确保格式正确
    processPrizes(prizes) {
      if (!prizes || !Array.isArray(prizes)) {
        return [];
      }

      return prizes.map((prize, index) => {
        const processedPrize = {
          ...prize,
          color: prize.color || this.getDefaultColor(index),
          isWin: prize.isWin !== undefined ? prize.isWin : (prize.productValue > 0),
          winningProbability: Number(prize.winningProbability) || 0,
          productValue: Number(prize.productValue) || 0
        };

        return processedPrize;
      });
    },

    // 获取默认颜色
    getDefaultColor(index) {
      const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA'];
      return colors[index % colors.length];
    },

    // 处理图片加载错误
    handleImageError(event) {
      const index = this.prizes.findIndex(p => p.picUrl === event.target.src);
      if (index !== -1 && this.fallbackImages[index]) {
        event.target.src = this.fallbackImages[index];
      } else {
        // 使用简单的SVG作为占位符
        const color = this.prizes[index]?.color || '#AA96DA';
        const name = this.prizes[index]?.prizeName || '奖品';
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
          <rect width="200" height="200" fill="${color}"/>
          <text x="100" y="100" font-family="Arial" font-size="18" text-anchor="middle" dy=".3em" fill="white">${name.substring(0, 8)}</text>
        </svg>`;
        event.target.src = 'data:image/svg+xml;base64,' + btoa(svg);
      }
    },

    // 加载所有图片
    loadAllImages() {
      console.log('开始加载所有奖品图片...');
      this.imageLoadPromises = [];
      this.allImagesLoaded = false;

      this.prizes.forEach((prize, index) => {
        const promise = new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'Anonymous'; // 注意：这里要大写A

          // 设置超时
          const timeout = setTimeout(() => {
            console.warn(`图片加载超时: ${prize.picUrl}`);
            img.src = this.fallbackImages[index] || this.fallbackImages[0];
            resolve(img);
          }, 5000);

          img.onload = () => {
            clearTimeout(timeout);
            console.log(`✅ 图片加载成功: ${prize.prizeName}`);
            // 将加载好的图片保存到奖品对象中
            this.prizes[index].loadedImage = img;
            resolve(img);
          };

          img.onerror = () => {
            clearTimeout(timeout);
            console.warn(`❌ 图片加载失败: ${prize.picUrl}，使用备用图片`);
            img.src = this.fallbackImages[index] || this.fallbackImages[0];
            this.prizes[index].loadedImage = img;
            resolve(img);
          };

          // 开始加载图片
          img.src = prize.picUrl;
        });

        this.imageLoadPromises.push(promise);
      });

      return Promise.all(this.imageLoadPromises).then(() => {
        this.allImagesLoaded = true;
        console.log('✅ 所有图片加载完成');
      });
    },

    // 初始化跑马灯
    initLights() {
      const container = this.$refs.lightsContainer;
      if (!container) return;

      container.innerHTML = '';
      const lightCount = 24;
      const radius = 180;

      for (let i = 0; i < lightCount; i++) {
        const light = document.createElement('div');
        light.className = 'light-dot';
        light.classList.add(i % 2 === 0 ? 'light-even' : 'light-odd');

        const angle = (i / lightCount) * 2 * Math.PI - Math.PI / 2;
        const x = Math.cos(angle) * radius + 180 - 6;
        const y = Math.sin(angle) * radius + 180 - 6;

        light.style.left = x + 'px';
        light.style.top = y + 'px';
        container.appendChild(light);
      }

      this.startLightAnimation();
    },

    // 开始跑马灯动画
    startLightAnimation() {
      const evenLights = document.querySelectorAll('.light-even');
      const oddLights = document.querySelectorAll('.light-odd');

      if (this.lightInterval) {
        clearInterval(this.lightInterval);
      }

      let toggle = false;
      this.lightInterval = setInterval(() => {
        toggle = !toggle;
        evenLights.forEach(el => el.classList.toggle('light-on', toggle));
        oddLights.forEach(el => el.classList.toggle('light-on', !toggle));
      }, 500);
    },

    // 初始化轮盘
    initWheel() {
      console.log('开始绘制轮盘...');
      const canvas = this.$refs.wheelCanvas;
      if (!canvas) {
        console.error('Canvas元素未找到');
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('Canvas上下文未获取到');
        return;
      }

      console.log('Canvas尺寸:', canvas.width, 'x', canvas.height);

      // 先绘制一个测试矩形，检查Canvas是否正常工作
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = this.wheelRadius;
      const sliceAngle = (2 * Math.PI) / this.prizes.length;

      // 清空画布
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      this.prizes.forEach((prize, index) => {
        const startAngle = index * sliceAngle - Math.PI / 2;
        const endAngle = startAngle + sliceAngle;
        const midAngle = startAngle + sliceAngle / 2;

        // 绘制扇区
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, radius, startAngle, endAngle);
        ctx.closePath();

        const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
        gradient.addColorStop(0, this.lightenColor(prize.color, 20));
        gradient.addColorStop(1, prize.color);
        ctx.fillStyle = gradient;
        ctx.fill();

        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 2;
        ctx.stroke();

        // 绘制图片和文字
        const imgRadius = radius * 0.6;
        const imgX = centerX + Math.cos(midAngle) * imgRadius;
        const imgY = centerY + Math.sin(midAngle) * imgRadius;
        const imgSize = 60;

        // 检查是否有已加载的图片
        const img = prize.loadedImage || new Image();

        if (prize.loadedImage) {
          // 如果图片已加载，直接绘制
          this.drawPrizeImage(ctx, img, imgX, imgY, imgSize, midAngle, prize.prizeName);
        } else {
          // 如果图片未加载，使用备用图片
          const fallbackImg = new Image();
          fallbackImg.src = this.fallbackImages[index] || this.fallbackImages[0];
          fallbackImg.onload = () => {
            this.drawPrizeImage(ctx, fallbackImg, imgX, imgY, imgSize, midAngle, prize.prizeName);
          };
        }
      });

      console.log('轮盘绘制完成');
    },

    // 绘制奖品图片和文字
    drawPrizeImage(ctx, img, x, y, size, angle, name) {
      try {
        // 绘制圆形图片
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, size/2, 0, 2 * Math.PI);
        ctx.clip();
        ctx.drawImage(img, x - size/2, y - size/2, size, size);
        ctx.restore();

        // 绘制奖品名称
        ctx.save();
        ctx.translate(x, y);
        let textRotation = angle + Math.PI / 2;
        ctx.rotate(textRotation);

        ctx.strokeStyle = '#000';
        ctx.lineWidth = 3;
        ctx.lineJoin = 'round';
        ctx.font = 'bold 14px Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.strokeText(name, 0, 0);

        ctx.fillStyle = '#FFF';
        ctx.fillText(name, 0, 0);
        ctx.restore();
      } catch (error) {
        console.error('绘制奖品图片失败:', error);
      }
    },

    // 颜色变亮函数
    lightenColor(color, percent) {
      const num = parseInt(color.replace('#', ''), 16);
      const amt = Math.round(2.55 * percent);
      const R = (num >> 16) + amt;
      const G = ((num >> 8) & 0x00FF) + amt;
      const B = (num & 0x000FF) + amt;
      return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 + (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 + (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
    },

    // 开始抽奖（入口方法，支持后台控制）
    startSpin() {
      if (this.isSpinning || this.isProcessing) return;

      if (this.currentCoins < this.costPerSpin) {
        this.triggerButtonShake();
        this.$emit('insufficient-coins', { currentCoins: this.currentCoins, cost: this.costPerSpin });
        return;
      }

      if (this.enableBackendControl) {
        this.isProcessing = true;

        this.$emit('request-prize', {
          onSuccess: (prizeIndex) => {
            this.isProcessing = false;
            this.executeSpinWithPrize(prizeIndex);
          },
          onError: (error) => {
            console.error('抽奖失败:', error);
            this.isProcessing = false;
            this.$emit('spin-error', error);
          }
        });
      } else {
        this.executeSpinWithPrize(this.getRandomPrizeIndex());
      }
    },

    // 获取随机奖品索引（前端控制模式使用）
    getRandomPrizeIndex() {
      const totalProbability = this.prizes.reduce((sum, prize) => sum + prize.winningProbability, 0);
      let random = Math.random() * 100;
      let cumulativeProbability = 0;
      let selectedPrizeIndex = this.prizes.length - 1;

      for (let i = 0; i < this.prizes.length; i++) {
        cumulativeProbability += this.prizes[i].winningProbability;
        if (random <= cumulativeProbability) {
          selectedPrizeIndex = i;
          break;
        }
      }

      return selectedPrizeIndex;
    },

    // 执行抽奖旋转（指定中奖索引）
    executeSpinWithPrize(prizeIndex) {
      if (prizeIndex < 0 || prizeIndex >= this.prizes.length) {
        console.error('中奖索引无效:', prizeIndex);
        this.$emit('spin-error', { message: '中奖索引无效', index: prizeIndex });
        return;
      }

      this.currentCoins -= this.costPerSpin;
      this.isSpinning = true;

      const sliceAngleDeg = 360 / this.prizes.length;
      const prizeCenterAngle = prizeIndex * sliceAngleDeg + sliceAngleDeg / 2;
      let targetAngle = 360 - prizeCenterAngle;

      const turns = 5;
      const totalRotation = this.currentRotation + (360 * turns) + targetAngle - (this.currentRotation % 360);

      this.currentRotation = totalRotation;

      setTimeout(() => {
        this.selectedPrize = this.prizes[prizeIndex];
        this.showResult();
        this.isSpinning = false;
        this.$emit('spin-result', {
          prize: this.selectedPrize,
          index: prizeIndex,
          coinsRemaining: this.currentCoins
        });
      }, 6000);
    },

    // 暴露给外部调用的中奖方法
    winPrize(prizeIndex) {
      if (this.isSpinning || this.isProcessing) {
        console.warn('轮盘正在旋转中，请稍后再试');
        return false;
      }

      if (this.currentCoins < this.costPerSpin) {
        this.triggerButtonShake();
        this.$emit('insufficient-coins', { currentCoins: this.currentCoins, cost: this.costPerSpin });
        return false;
      }

      this.executeSpinWithPrize(prizeIndex);
      return true;
    },

    // 显示结果
    showResult() {
      this.showPopup = true;
      this.$nextTick(() => {
        setTimeout(() => {
          this.popupAnimationClass = 'popup-visible';
        }, 50);

        if (this.selectedPrize.productValue > 0) {
          this.createConfetti(true);
        }
      });
    },

    // 关闭弹窗
    closePopup() {
      this.popupAnimationClass = 'popup-hidden';
      setTimeout(() => {
        this.showPopup = false;
      }, 300);
    },

    // 触发按钮抖动效果
    triggerButtonShake() {
      const btn = document.querySelector('.spin-button');
      if (btn) {
        btn.classList.add('shake');
        setTimeout(() => btn.classList.remove('shake'), 500);
      }
    },

    // 创建彩色纸屑
    createConfetti(show) {
      if (!show) return;

      const colors = ['#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3', '#F38181', '#AA96DA'];

      for (let i = 0; i < 100; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti';
        confetti.style.left = Math.random() * 100 + 'vw';
        confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        confetti.style.animationDelay = Math.random() * 2 + 's';
        confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
        const size = Math.random() * 10 + 5;
        confetti.style.width = size + 'px';
        confetti.style.height = size + 'px';
        document.body.appendChild(confetti);
        setTimeout(() => confetti.remove(), 5000);
      }
    },

    // 添加金币
    addCoins(amount) {
      this.currentCoins += amount;
      this.$emit('coins-added', { amount, total: this.currentCoins });
    },

    // 重置游戏
    resetGame() {
      this.currentCoins = this.initialCoins;
      this.currentRotation = 0;
      this.isSpinning = false;
      this.isProcessing = false;
      this.$emit('game-reset');
    },

    // 设置奖品列表
    setPrizes(prizes) {
      this.prizes = this.processPrizes(prizes);
      this.$nextTick(() => {
        this.loadAllImages().then(() => {
          this.initWheel();
        });
      });
    },

    // 获取当前金币数量
    getCurrentCoins() {
      return this.currentCoins;
    },

    // 获取抽奖状态
    getSpinStatus() {
      return {
        isSpinning: this.isSpinning,
        isProcessing: this.isProcessing,
        currentCoins: this.currentCoins
      };
    },

    // 获取奖品列表
    getPrizeList() {
      return this.prizes;
    },

    // 计算总概率
    getTotalProbability() {
      return this.prizes.reduce((sum, prize) => sum + prize.winningProbability, 0);
    },

    // 强制重新绘制轮盘
    redrawWheel() {
      console.log('强制重新绘制轮盘');
      this.initWheel();
    }
  }
};
</script>

<style scoped>
/* 弹窗专用容器 - 去除全屏背景 */
.lottery-container {
  font-family: 'Inter', 'Helvetica Neue', Arial, sans-serif;
  background: transparent;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
  position: relative;
  overflow: visible;
  box-sizing: border-box;
  border-radius: 20px;
}

.lottery-container.spinning {
  cursor: wait;
}

/* 标题样式 - 紧凑 */
.lottery-header {
  text-align: center;
  margin-bottom: 15px;
  position: relative;
  z-index: 10;
  width: 100%;
}

.lottery-header h1 {
  font-size: 20px;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}

.gift-icon {
  color: #f472b6;
  margin-right: 8px;
  font-size: 18px;
}

.subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  margin: 0;
}

/* 轮盘主体 */
.wheel-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  width: 340px;
  height: 340px;
  margin: 0 auto;
}

/* 跑马灯容器 */
.lights-container {
  position: absolute;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  z-index: 1;
  pointer-events: none;
}

/* 单个灯泡 */
.light-dot {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
  transition: all 0.2s;
}

/* 亮灯状态 */
.light-on {
  background-color: #FFD700;
  box-shadow: 0 0 10px #FFD700, 0 0 20px #FFA500;
}

/* 轮盘容器 */
.wheel-container {
  position: relative;
  width: 320px;
  height: 320px;
  border-radius: 50%;
  border: 8px solid #fff;
  box-shadow: 0 0 15px rgba(0,0,0,0.5), inset 0 0 8px rgba(0,0,0,0.2);
  z-index: 2;
  overflow: hidden;
  background: #fff;
}

.wheel-canvas {
  width: 100%;
  height: 100%;
  transition: transform 6s cubic-bezier(0.2, 0.8, 0.3, 1);
  display: block;
  background: #fff;
}

/* 中心交互区域 */
.center-hub {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 30;
  width: 160px;
  height: 160px;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

/* 指针样式 */
.center-pointer {
  position: absolute;
  top: calc(50% - 30px);
  left: 50%;
  transform: translate(-50%, -100%);
  width: 0;
  height: 0;
  border-left: 15px solid transparent;
  border-right: 15px solid transparent;
  border-bottom: 30px solid #FF4D4D;
  filter: drop-shadow(0 4px 4px rgba(0,0,0,0.3));
  z-index: 20;
}

/* 抽奖按钮 */
.spin-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #FF4D4D 0%, #F00000 100%);
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: 4px solid #fff;
  transition: all 0.2s ease;
  pointer-events: auto;
  z-index: 10;
  color: white;
  font-weight: 900;
  font-size: 18px;
  text-shadow: 0 1px 1px rgba(0,0,0,0.2);
  outline: none;
  font-family: inherit;
}

.spin-button:hover:not(.disabled) {
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 0 20px rgba(255, 77, 77, 0.6);
}

.spin-button.disabled {
  opacity: 0.8;
  cursor: not-allowed;
  transform: translate(-50%, -50%);
}

.spin-button.disabled:hover {
  transform: translate(-50%, -50%);
  box-shadow: 0 4px 10px rgba(0,0,0,0.3);
}

.spinning-text {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.spinner-icon {
  margin-right: 6px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 金币信息 */
.coins-info {
  margin-top: 15px;
  text-align: center;
  position: relative;
  z-index: 10;
}

.coins-display {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.coins-icon {
  color: #fbbf24;
  font-size: 16px;
  margin-right: 6px;
}

.coins-text {
  color: white;
  font-size: 14px;
}

.coins-amount {
  font-weight: bold;
  color: #fbbf24;
  font-size: 16px;
}

.separator {
  color: rgba(255, 255, 255, 0.4);
  margin: 0 10px;
  font-size: 14px;
}

.cost-text {
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
}

.cost-amount {
  font-weight: bold;
  color: white;
  font-size: 14px;
}

/* 结果弹窗 - 精简优化 */
.result-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

.overlay-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
}

.result-popup-content {
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  max-width: 280px;
  position: relative;
  z-index: 10;
  box-sizing: border-box;
}

.popup-hidden {
  transform: scale(0.95);
  opacity: 0;
}

.popup-visible {
  transform: scale(1);
  opacity: 1;
}

.popup-win {
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 140, 0, 0.2) 100%);
  border-color: rgba(255, 215, 0, 0.5);
  box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
}

.popup-lose {
  background: rgba(100, 116, 139, 0.2);
  border-color: rgba(148, 163, 184, 0.2);
}

.popup-inner {
  text-align: center;
}

.popup-icon {
  width: 50px;
  height: 50px;
  margin: 0 auto 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-icon-win {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
}

.popup-icon-lose {
  background: rgba(75, 85, 99, 0.5);
}

.popup-icon i {
  font-size: 1.5rem;
}

.popup-icon-win i {
  color: #fde047;
}

.popup-icon-lose i {
  color: #9ca3af;
}

.popup-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: white;
  margin-bottom: 6px;
  line-height: 1.2;
}

.popup-subtitle {
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 12px;
  font-size: 0.8rem;
  line-height: 1.2;
}

.prize-result {
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
}

.prize-result-win {
  background: rgba(255, 255, 255, 0.1);
}

.prize-result-lose {
  background: rgba(255, 255, 255, 0.05);
}

.prize-image-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.prize-image {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: block;
  background: #fff;
}

.prize-name {
  font-size: 1rem;
  font-weight: 600;
  color: white;
  margin: 0 0 4px 0;
  line-height: 1.2;
  word-break: break-word;
}

.prize-value {
  font-size: 0.85rem;
  color: #fbbf24;
  font-weight: 500;
  margin: 0;
}

.popup-btn {
  width: 100%;
  padding: 8px;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.3s;
  font-size: 0.85rem;
  font-family: inherit;
  outline: none;
  box-sizing: border-box;
}

.popup-btn:hover {
  opacity: 0.9;
}

.popup-btn-win {
  background: linear-gradient(to right, #ec4899, #8b5cf6);
}

.popup-btn-lose {
  background: #6b7280;
}

/* 动画 */
@keyframes confetti {
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
}

.confetti {
  position: fixed;
  width: 10px;
  height: 10px;
  top: -10px;
  animation: confetti 3s ease-in-out forwards;
  z-index: 9999;
}

@keyframes buttonShake {
  0%, 100% { transform: translate(-50%, -50%) translateX(0); }
  25% { transform: translate(-50%, -50%) translateX(-5px); }
  50% { transform: translate(-50%, -50%) translateX(5px); }
  75% { transform: translate(-50%, -50%) translateX(-5px); }
}

.shake {
  animation: buttonShake 0.5s ease-in-out;
}

/* 弹窗过渡动画 */
.popup-fade-enter-active, .popup-fade-leave-active {
  transition: opacity 0.3s;
}
.popup-fade-enter, .popup-fade-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .wheel-wrapper {
    width: 300px;
    height: 300px;
  }

  .lights-container {
    width: 320px;
    height: 320px;
  }

  .wheel-container {
    width: 280px;
    height: 280px;
  }

  .lottery-header h1 {
    font-size: 18px;
  }

  .gift-icon {
    font-size: 16px;
  }

  .subtitle {
    font-size: 11px;
  }

  .coins-display {
    padding: 6px 12px;
  }

  .coins-text, .coins-amount {
    font-size: 13px;
  }

  .cost-text, .cost-amount {
    font-size: 11px;
  }

  /* 结果弹窗响应式 */
  .result-popup-content {
    max-width: 260px;
    padding: 14px;
  }

  .popup-icon {
    width: 45px;
    height: 45px;
  }

  .popup-icon i {
    font-size: 1.3rem;
  }

  .popup-title {
    font-size: 1rem;
  }

  .popup-subtitle {
    font-size: 0.75rem;
  }

  .prize-result {
    padding: 10px;
  }

  .prize-image {
    width: 55px;
    height: 55px;
  }

  .prize-name {
    font-size: 0.95rem;
  }

  .prize-value {
    font-size: 0.8rem;
  }

  .popup-btn {
    padding: 7px;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .wheel-wrapper {
    width: 280px;
    height: 280px;
  }

  .lights-container {
    width: 300px;
    height: 300px;
  }

  .wheel-container {
    width: 260px;
    height: 260px;
  }

  /* 结果弹窗响应式 - 小屏幕 */
  .result-popup-content {
    max-width: 240px;
    padding: 12px;
  }

  .popup-icon {
    width: 40px;
    height: 40px;
    margin-bottom: 6px;
  }

  .popup-icon i {
    font-size: 1.2rem;
  }

  .popup-title {
    font-size: 0.95rem;
  }

  .popup-subtitle {
    font-size: 0.7rem;
    margin-bottom: 10px;
  }

  .prize-result {
    padding: 8px;
    margin-bottom: 10px;
  }

  .prize-image {
    width: 50px;
    height: 50px;
  }

  .prize-name {
    font-size: 0.9rem;
  }

  .prize-value {
    font-size: 0.75rem;
  }

  .popup-btn {
    padding: 6px;
    font-size: 0.75rem;
  }
}
</style>